import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { settingsService } from '../services/api'
import { t } from '../utils/translations'

const Invoice = ({ order, isPrintMode = false }) => {
  const { data: settings } = useQuery({
    queryKey: ['storeSettings'],
    queryFn: settingsService.getSettings,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  if (!order) return null

  const logoUrl = settings?.logo ? `/uploads/settings/${settings.logo}` : null
  const storeName = settings?.store_name || 'سبحات'
  const storeAddress = settings?.address || ''
  const storePhone = settings?.phone_number || ''
  const storeEmail = settings?.email || ''

  const printStyles = isPrintMode ? {
    '@media print': {
      body: { margin: 0, padding: 0, fontSize: '12pt' },
      '.no-print': { display: 'none' },
      '.invoice-container': { 
        maxWidth: 'none', 
        boxShadow: 'none', 
        border: 'none',
        padding: '20px'
      }
    }
  } : {}

  return (
    <div 
      className={`invoice-container bg-white ${isPrintMode ? 'p-8' : 'p-6'} max-w-4xl mx-auto`}
      style={printStyles}
    >
      {/* Invoice Header */}
      <div className="flex justify-between items-start mb-8">
        <div className="flex items-center space-x-4 space-x-reverse">
          {logoUrl && (
            <img 
              src={logoUrl} 
              alt={storeName}
              className="w-16 h-16 object-contain"
            />
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{storeName}</h1>
            <div className="text-sm text-gray-600">
              {storePhone && <p>{t('phone')}: {storePhone}</p>}
              {storeEmail && <p>{t('email')}: {storeEmail}</p>}
            </div>
          </div>
        </div>
        
        <div className="text-left">
          <h2 className="text-xl font-semibold text-gray-900">{t('invoice').toUpperCase()}</h2>
          <p className="text-lg font-medium text-gray-700">#{order.order_number}</p>
          <p className="text-sm text-gray-600">
            {t('order_number')}: #{order.order_number}
          </p>
        </div>
      </div>

      {/* Customer Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('bill_to')}:</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="font-medium">
              {order.customer_name || 
               (order.first_name && order.last_name ? `${order.first_name} ${order.last_name}` : null) ||
               (order.billing_address?.first_name && order.billing_address?.last_name ? 
                `${order.billing_address.first_name} ${order.billing_address.last_name}` : null) ||
               'Customer'
              }
            </p>
            <p className="text-gray-600">{order.customer_email || order.email || order.billing_address?.email || ''}</p>
            {(order.customer_phone || order.phone || order.billing_address?.phone) && (
              <p className="text-gray-600">{t('contact_info')}: {order.customer_phone || order.phone || order.billing_address?.phone}</p>
            )}
            {(order.billing_address?.address || order.shipping_address?.address || order.shipping_address) && (
              <div className="mt-2">
                <p className="text-sm font-medium text-gray-700">{t('shipping_address')}:</p>
                <p className="text-gray-600">
                  {order.billing_address?.address || 
                   order.shipping_address?.address || 
                   (typeof order.shipping_address === 'string' ? order.shipping_address : JSON.stringify(order.shipping_address?.address || ''))
                  }
                </p>
                <p className="text-gray-600">
                  {order.city_name || order.city || order.billing_address?.city || order.shipping_address?.city || 'N/A'}, {order.governorate_name || 'N/A'}
                </p>
                <p className="text-gray-600">
                  {order.country_name || 'N/A'}
                </p>
              </div>
            )}
          </div>
        </div>
        
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('order_details')}:</h3>
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('order_number')}:</span>
              <span className="font-medium">#{order.order_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('order_date')}:</span>
              <span>#{order.order_number}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('payment_method')}:</span>
              <span className="capitalize">{order.payment_method === 'cash' ? t('cash_on_delivery') : order.payment_method || t('cash_on_delivery')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">{t('status')}:</span>
              <span className={`px-2 py-1 text-xs rounded-full ${
                order.status === 'completed' ? 'bg-green-100 text-green-800' :
                order.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {t(order.status) || order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('items_ordered')}:</h3>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-50">
                <th className="border border-gray-300 px-4 py-3 text-right text-gray-900 font-semibold">Product ID</th>
                <th className="border border-gray-300 px-4 py-3 text-right text-gray-900 font-semibold">{t('item')}</th>
                <th className="border border-gray-300 px-4 py-3 text-center text-gray-900 font-semibold">{t('quantity')}</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-gray-900 font-semibold">{t('unit_price')}</th>
                <th className="border border-gray-300 px-4 py-3 text-left text-gray-900 font-semibold">{t('total')}</th>
              </tr>
            </thead>
            <tbody>
              {order.items && order.items.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="border border-gray-300 px-4 py-3 text-center text-gray-900">
                    #{item.product_id}
                  </td>
                  <td className="border border-gray-300 px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{item.name || item.product_name}</p>
                      
                      {/* Display selected_attributes from JSON */}
                      {item.selected_attributes && Object.keys(item.selected_attributes).length > 0 && (
                        <div className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">{t('attributes')}: </span>
                          {Object.entries(item.selected_attributes).map(([key, value]) => `${key}: ${value}`).join(', ')}
                        </div>
                      )}
                      
                      {/* Display attribute information from joins */}
                      {(item.attribute_name || item.attribute_variation_name) && (
                        <div className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">{t('attributes')}: </span>
                          {item.attribute_name && `${item.attribute_name}`}
                          {item.attribute_variation_name && `: ${item.attribute_variation_name}`}
                          {item.attribute_variation_value && ` (${item.attribute_variation_value})`}
                        </div>
                      )}
                      
                      {/* Display legacy attributes field */}
                      {item.attributes && Object.keys(item.attributes).length > 0 && (
                        <div className="text-sm text-gray-600 mt-1">
                          <span className="font-medium">{t('attributes')}: </span>
                          {Object.entries(item.attributes).map(([key, value]) => `${key}: ${value}`).join(', ')}
                        </div>
                      )}
                      
                      {(item.product_sku || item.sku) && (
                        <p className="text-xs text-gray-500 mt-1">SKU: {item.product_sku || item.sku}</p>
                      )}
                    </div>
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-center text-gray-900">
                    {item.quantity}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-left text-gray-900">
                    {settings?.currency_symbol || 'KD'}{parseFloat(item.price || item.unit_price || 0).toFixed(2)}
                  </td>
                  <td className="border border-gray-300 px-4 py-3 text-left font-medium text-gray-900">
                    {settings?.currency_symbol || 'KD'}{(parseFloat(item.price || item.unit_price || 0) * parseInt(item.quantity)).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Order Totals */}
      <div className="flex justify-start mb-8">
        <div className="w-full max-w-md">
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">{t('subtotal')}:</span>
              <span>{settings?.currency_symbol || 'KD'}{(parseFloat(order.total_amount || 0) - parseFloat(order.shipping_amount || 0)).toFixed(2)}</span>
            </div>
            
            {order.discount_amount && parseFloat(order.discount_amount) > 0 && (
              <div className="flex justify-between text-red-600">
                <span>{t('discount')}:</span>
                <span>-{settings?.currency_symbol || 'KD'}{parseFloat(order.discount_amount).toFixed(2)}</span>
              </div>
            )}
            
            {order.tax_amount && parseFloat(order.tax_amount) > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-600">{t('tax')}:</span>
                <span>{settings?.currency_symbol || 'KD'}{parseFloat(order.tax_amount).toFixed(2)}</span>
              </div>
            )}
            
            <div className="flex justify-between">
              <span className="text-gray-600">{t('shipping')}:</span>
              <span>{settings?.currency_symbol || 'KD'}{parseFloat(order.shipping_amount || 0).toFixed(2)}</span>
            </div>
            
            <hr className="border-gray-300" />
            <div className="flex justify-between text-lg font-bold">
              <span>{t('total')}:</span>
              <span>{settings?.currency_symbol || 'KD'}{parseFloat(order.total_amount || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('notes')}:</h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-gray-700">{order.notes}</p>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="text-center text-sm text-gray-600 border-t pt-4">
        <p>{t('thank_you')}</p>
        {storePhone && <p>للاستفسارات حول هذه الفاتورة، يرجى الاتصال بنا على {storePhone}</p>}
      </div>
    </div>
  )
}

export default Invoice
